import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const SQRT3 = Math.sqrt(3);

const WyeDeltaConverter = () => {
  const { dark } = useTheme();
  const [conn, setConn] = useState('wye');   // 'wye' | 'delta'
  // Wye inputs
  const [vln, setVln] = useState(7.967);     // kV
  const [il_wye, setIlWye] = useState(209.2); // A
  // Delta inputs
  const [vll_delta, setVllDelta] = useState(13.8);  // kV
  const [iph_delta, setIphDelta] = useState(120.8); // A

  const wyeCalc = useMemo(() => ({
    vll: vln * SQRT3,
    iphase: il_wye, // In wye: I_line = I_phase
  }), [vln, il_wye]);

  const deltaCalc = useMemo(() => ({
    vphase: vll_delta, // In delta: V_phase = V_LL
    il: iph_delta * SQRT3,
  }), [vll_delta, iph_delta]);

  const axisColor   = dark ? '#64748b' : '#94a3b8';
  const gridColor   = dark ? '#1e293b' : '#f1f5f9';
  const tooltipBg   = dark ? '#1e293b' : '#ffffff';
  const tooltipBorder = dark ? '#334155' : '#e2e8f0';

  return (
    <div data-testid="wye-delta-converter" className="space-y-5">
      {/* Connection selector */}
      <div className="flex gap-3">
        {[
          { key: 'wye', label: 'Wye (Y) Connection' },
          { key: 'delta', label: 'Delta (Δ) Connection' },
        ].map(({ key, label }) => (
          <button
            key={key}
            data-testid={`conn-btn-${key}`}
            onClick={() => setConn(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
              conn === key
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-teal-400'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {conn === 'wye' ? (
        <div className="space-y-5">
          <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg p-3 text-xs text-teal-800 dark:text-teal-300">
            <strong>Wye rule:</strong> V_LL = √3 × V_LN at +30°  |  I_line = I_phase (same conductor carries winding current)
          </div>

          {/* Voltage input */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  V<sub>LN</sub> — Line-to-Neutral Voltage (kV)
                </label>
                <TeachingTooltip
                  showIcon
                  whatThis="The voltage from any one phase conductor to the neutral point N of a wye-connected source. This is the phase voltage for wye. For a 13.8 kV system: V_LN = 13.8 / √3 = 7.967 kV."
                  tryThis="Enter 7.967 kV and watch V_LL calculate to 13.8 kV — the standard 13.8 kV distribution voltage is a line-to-line value."
                  whyMatters="Transformer secondary nameplate voltages are line-to-line. Relay engineers must know V_LN for phase-to-ground relay settings."
                  connectedTo="→ Updates V_LL output and the bar chart comparison below."
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid="vln-input"
                  type="range" min="0.1" max="200" step="0.001" value={vln}
                  onChange={e => setVln(Number(e.target.value))}
                  className="flex-1 accent-teal-600"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number" value={vln} step="0.001" min="0.1"
                    onChange={e => setVln(Math.max(0.001, Number(e.target.value)))}
                    className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
                  />
                  <span className="text-xs text-slate-500 w-7">kV</span>
                </div>
              </div>
            </div>

            {/* Current input */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  I<sub>L</sub> — Line Current (A)
                </label>
                <TeachingTooltip
                  showIcon
                  whatThis="The current flowing in each line conductor of a wye-connected system. In wye, this is identical to the winding (phase) current — there is only one path."
                  tryThis="Set I_L = 418.4 A — that is I_base for a 100 MVA, 138 kV system. The phase current will show the same value."
                  whyMatters="Transformer and conductor ampacity ratings are specified as line currents. Protective relay CTs are sized and connected to line currents."
                  connectedTo="→ I_phase output (equals I_L in wye). Used in S = √3 × V_LL × I_L."
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid="il-wye-input"
                  type="range" min="1" max="3000" step="0.1" value={il_wye}
                  onChange={e => setIlWye(Number(e.target.value))}
                  className="flex-1 accent-teal-600"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number" value={il_wye} min="0" step="0.1"
                    onChange={e => setIlWye(Math.max(0, Number(e.target.value)))}
                    className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
                  />
                  <span className="text-xs text-slate-500 w-5">A</span>
                </div>
              </div>
            </div>
          </div>

          {/* Wye outputs */}
          <div className="grid grid-cols-2 gap-4">
            <div data-testid="vll-result" className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4 text-center">
              <p className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-widest mb-1">V<sub>LL</sub> = √3 × V<sub>LN</sub></p>
              <p className="text-2xl font-bold text-teal-900 dark:text-teal-100 font-mono">{wyeCalc.vll.toFixed(3)}</p>
              <p className="text-xs text-teal-600 dark:text-teal-500">kV (line-to-line)</p>
            </div>
            <div data-testid="iphase-wye-result" className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">I<sub>phase</sub> = I<sub>L</sub></p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{wyeCalc.iphase.toFixed(1)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-500">A (same in wye)</p>
            </div>
          </div>

          {/* Voltage bar chart */}
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">Voltage comparison: V_LN vs V_LL</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={[
                  { name: 'V_LN', val: parseFloat(vln.toFixed(3)) },
                  { name: 'V_LL', val: parseFloat(wyeCalc.vll.toFixed(3)) },
                ]}
                margin={{ top: 10, right: 30, bottom: 4, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={{ stroke: axisColor }} tickFormatter={v => `${v} kV`} />
                <Tooltip contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 6, fontSize: 11 }} formatter={v => [`${v} kV`]} />
                <Bar dataKey="val" radius={[4, 4, 0, 0]}>
                  <Cell fill="#64748b" />
                  <Cell fill="#0d9488" />
                  <LabelList dataKey="val" position="top" style={{ fontSize: 10, fill: axisColor }} formatter={v => `${v} kV`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-center text-slate-400 dark:text-slate-600 mt-1">
              Ratio V_LL / V_LN = √3 = 1.7321 always (wye connection)
            </p>
          </div>
        </div>

      ) : (
        <div className="space-y-5">
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
            <strong>Delta rule:</strong> V_phase = V_LL (load is connected directly across lines)  |  I_L = √3 × I_phase at −30°
          </div>

          {/* Delta inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  V<sub>LL</sub> = V<sub>phase</sub> (kV)
                </label>
                <TeachingTooltip
                  showIcon
                  whatThis="In a delta connection, each winding is connected directly between two line conductors. Therefore the phase voltage equals the line-to-line voltage — there is no neutral point."
                  tryThis="Enter 13.8 kV — the phase voltage across each delta winding is the full line-to-line 13.8 kV."
                  whyMatters="Delta windings block zero-sequence current, which is why delta-connected transformers are used to isolate ground faults in distribution systems."
                  connectedTo="→ V_phase output. The phase voltage drives current through each delta impedance branch."
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid="vll-delta-input"
                  type="range" min="0.1" max="765" step="0.1" value={vll_delta}
                  onChange={e => setVllDelta(Number(e.target.value))}
                  className="flex-1 accent-teal-600"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number" value={vll_delta} step="0.1" min="0.1"
                    onChange={e => setVllDelta(Math.max(0.1, Number(e.target.value)))}
                    className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
                  />
                  <span className="text-xs text-slate-500 w-7">kV</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  I<sub>phase</sub> — Delta Branch Current (A)
                </label>
                <TeachingTooltip
                  showIcon
                  whatThis="The current flowing through each branch of the delta — i.e. through each winding or load element connected between two line conductors."
                  tryThis="Enter I_phase = 50 A and watch I_L calculate to 86.6 A — that is √3 × 50."
                  whyMatters="CTs are always installed on line conductors (measuring I_L), not on delta branches. To find I_phase from a CT reading, divide by √3."
                  connectedTo="→ Updates I_L = √3 × I_phase and the bar chart comparison below."
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid="iphase-delta-input"
                  type="range" min="1" max="2000" step="0.1" value={iph_delta}
                  onChange={e => setIphDelta(Number(e.target.value))}
                  className="flex-1 accent-teal-600"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number" value={iph_delta} min="0" step="0.1"
                    onChange={e => setIphDelta(Math.max(0, Number(e.target.value)))}
                    className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
                  />
                  <span className="text-xs text-slate-500 w-5">A</span>
                </div>
              </div>
            </div>
          </div>

          {/* Delta outputs */}
          <div className="grid grid-cols-2 gap-4">
            <div data-testid="vphase-delta-result" className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-center">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-widest mb-1">V<sub>phase</sub> = V<sub>LL</sub></p>
              <p className="text-2xl font-bold text-amber-900 dark:text-amber-100 font-mono">{deltaCalc.vphase.toFixed(3)}</p>
              <p className="text-xs text-amber-600 dark:text-amber-500">kV (across each winding)</p>
            </div>
            <div data-testid="il-delta-result" className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4 text-center">
              <p className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-widest mb-1">I<sub>L</sub> = √3 × I<sub>phase</sub></p>
              <p className="text-2xl font-bold text-teal-900 dark:text-teal-100 font-mono">{deltaCalc.il.toFixed(1)}</p>
              <p className="text-xs text-teal-600 dark:text-teal-500">A (line current)</p>
            </div>
          </div>

          {/* Current bar chart */}
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">Current comparison: I_phase vs I_L</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={[
                  { name: 'I_phase', val: parseFloat(iph_delta.toFixed(1)) },
                  { name: 'I_L', val: parseFloat(deltaCalc.il.toFixed(1)) },
                ]}
                margin={{ top: 10, right: 30, bottom: 4, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={{ stroke: axisColor }} tickFormatter={v => `${v} A`} />
                <Tooltip contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 6, fontSize: 11 }} formatter={v => [`${v} A`]} />
                <Bar dataKey="val" radius={[4, 4, 0, 0]}>
                  <Cell fill="#64748b" />
                  <Cell fill="#0d9488" />
                  <LabelList dataKey="val" position="top" style={{ fontSize: 10, fill: axisColor }} formatter={v => `${v} A`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-center text-slate-400 dark:text-slate-600 mt-1">
              Ratio I_L / I_phase = √3 = 1.7321 always (delta connection)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WyeDeltaConverter;
